import type { Portfolio } from "@/types/Portfolio";
import type { DemoWatchlistSpec } from "@/pages/PortfolioBuilder/demoWatchlist";
import type { FilterDraft } from "@/pages/PortfolioBuilder/hooks/useFilterBuilder";
import { emptyNotifications } from "@/types/notifications";
import type { RecordedWatchlist } from "./index";

/**
 * A recorded plan, read back out in the two shapes the demo needs it in.
 *
 * The demo told a visitor a watchlist had been built for them and then never
 * showed it, and offered the panels a different list than the one the builder
 * displayed. Both come from here now, off one resolution of the plan's member
 * lists, so they cannot say different things:
 *
 *   recordedFilterDraft  — the combine editor's own inputs, so the demo can
 *                          show the real editor rather than a summary of it.
 *   recordedWatchlistSpec — the same combination flattened into the single
 *                          list the filter and highlight menus can offer.
 *
 * Nothing here resolves, fetches or saves. The visitor is looking at a
 * recorded answer, and the lists it cites were resolved to their real contents
 * at record time into `recorded.library` — a logged-out visitor's own fetch is
 * 403, which is what made the live route impossible and this one necessary.
 *
 * Pure and type-only by design: the guided run's tests run under vitest's
 * `node` environment, so anything reachable from here must not touch `window`.
 */

/** Placeholder ids for objects that exist only in this render. */
const DEMO_VIEW_ID = -900;

/**
 * A plan-authored list, shaped as a Portfolio so the viewer can draw it.
 *
 * Negative, descending ids: the modal keys rows on `portfolio.id`, so two
 * created lists sharing one id would collapse into a single row. Nothing here
 * is ever persisted or matched against a real record.
 */
function toPortfolio(
  source: { name: string; tickers: string[]; keywords: string[] },
  index: number
): Portfolio {
  return {
    id: DEMO_VIEW_ID - index,
    name: source.name,
    tickers: source.tickers,
    keywords: source.keywords,
    color: "#00aeef",
    notifications: emptyNotifications(),
    customizations: { includeRelatedHeadlines: false },
    owner_type: "NEWSQUAWK",
  };
}

/**
 * The lists a recorded combination is made of, resolved to real contents.
 *
 * Two ref namespaces, resolved from two places. `lib:<name>` cites a Newsquawk
 * library list that already exists; its real contents were resolved at record
 * time into `recorded.library`, because a logged-out visitor's own fetch is
 * empty and the rows would otherwise be names with nothing behind them.
 * Anything else is a list this plan creates, and only the plan knows what is
 * in it.
 *
 * Shared by both readers below so the watchlist the panels offer and the
 * watchlist the builder displays can never drift apart — which is the bug this
 * was extracted to prevent.
 */
function memberLists(recorded: RecordedWatchlist): Portfolio[] {
  const query = recorded.plan.headline_queries[0];
  if (!query) return [];

  const libraryByName = new Map(recorded.library?.map((p) => [p.name, p]) ?? []);
  const createdByRef = new Map(recorded.plan.portfolios.map((p) => [p.ref, p]));

  const portfolios: Portfolio[] = [];
  query.portfolio_refs.forEach((ref, index) => {
    if (ref.startsWith("lib:")) {
      // Absent means the recording predates the list being resolved, not that
      // the plan is malformed — skip the row rather than drawing an empty one.
      const existing = libraryByName.get(ref.slice(4));
      if (existing) portfolios.push(existing);
      return;
    }
    const created = createdByRef.get(ref);
    if (created) portfolios.push(toPortfolio(created, index));
  });
  return portfolios;
}

/**
 * The recording as the combine builder's own inputs.
 *
 * The demo shows this in the real editor rather than a read-only summary,
 * because the editor IS the thing worth showing — the point of the step is
 * what the product can build, and a bespoke preview of it only proves we can
 * draw a list. Everything here is fixed: the visitor is looking at a recorded
 * answer, so nothing needs to resolve, fetch or save.
 *
 * `portfolios` are handed to the builder directly rather than through
 * usePortfoliosCRUD — same shape of override PlanPreview already takes for the
 * same reason, and it keeps the demo's lists out of every filter panel on the
 * dashboard.
 */
export function recordedFilterDraft(
  recorded: RecordedWatchlist
): { draft: FilterDraft; portfolios: Portfolio[] } | null {
  const query = recorded.plan.headline_queries[0];
  if (!query) return null;

  const portfolios = memberLists(recorded);
  return {
    portfolios,
    draft: {
      name: query.name,
      searchKeyword: query.search_keyword || undefined,
      tags: query.tags,
      portfolioIds: portfolios.map((p) => p.id),
      filterMode: query.filter_op,
      keywordOp: query.keyword_op,
    },
  };
}

/**
 * The same combination, flattened into the one list the panels can offer.
 *
 * The filter and highlight menus take watchlists, not queries, so a
 * combination has to arrive as a single list — its members' terms unioned,
 * under the combination's own name.
 *
 * That name is the point. The demo seeded the panels with the asset-class template's
 * list ("Magnificent 7") while the builder showed a different one being built
 * ("Megacap Tech, Chips & AI Capex"), so the step that says "apply that
 * watchlist to the feed" pointed at a menu where that watchlist did not
 * appear. Every asset class had this — five asset classes, five mismatched pairs.
 *
 * Tags travel too: Portfolio.tags joins the same comparison against a
 * headline's tag_id_list that any other tag makes, and a recorded combination
 * can lean on them heavily (the FX plan is five lists and ten tags), so
 * dropping them would leave the applied list quietly matching less than the
 * one the builder displayed.
 */
export function recordedWatchlistSpec(
  recorded: RecordedWatchlist
): DemoWatchlistSpec | null {
  const query = recorded.plan.headline_queries[0];
  if (!query) return null;

  const members = memberLists(recorded);
  const union = (pick: (p: Portfolio) => string[] | undefined) => [
    ...new Set(members.flatMap((p) => pick(p) ?? [])),
  ];

  return {
    name: query.name,
    tickers: union((p) => p.tickers),
    keywords: union((p) => p.keywords),
    // The query's own tags, plus any the member lists resolved to.
    tags: [...new Set([...query.tags, ...union((p) => p.tags)])],
  };
}
